import React, { useEffect, useState, useCallback } from 'react';
import { LOAD_STATUSES, LOAD_STATUS_COLORS, LOAD_STATUS_LABELS } from '../lib/loadStatus';
import axios from 'axios';
import {
  MapPin, Package, PackageCheck, Truck, CheckCircle, Circle, Loader2,
  Navigation, Clock, Zap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = Object.fromEntries(LOAD_STATUSES.map((k) => {
  const [bg, fg] = LOAD_STATUS_COLORS[k].split(' ');
  return [k, { label: LOAD_STATUS_LABELS[k].toUpperCase(), bg, fg }];
}));

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const t = new Date();
  if (d.toDateString() === t.toDateString()) return 'Today';
  const y = new Date(t); y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const askGeo = () =>
  new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60_000 },
    );
  });

/**
 * StopProgressCard
 * Renders the per-stop check-in flow for a single load.
 * Props: { loadId, readOnly?: bool, onStatusChange?: fn }
 */
const StopProgressCard = ({ loadId, readOnly = false, onStatusChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // {seq, action}
  const [err, setErr] = useState('');

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/loads/${loadId}/stops/progress`, { withCredentials: true });
      setData(res.data);
      if (onStatusChange) onStatusChange(res.data.status);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  }, [loadId, onStatusChange]);

  useEffect(() => { refresh(); }, [refresh]);

  const checkIn = async (seq, action, kind) => {
    setPending({ seq, action });
    setErr('');
    const coords = action === 'arrived' ? await askGeo() : null;
    try {
      const body = { action, kind };
      if (coords) { body.lat = coords.lat; body.lng = coords.lng; }
      await axios.post(`${API}/loads/${loadId}/stops/${seq}/check-in`, body, { withCredentials: true });
      await refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Check-in failed');
    } finally {
      setPending(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-2 text-sm text-slate-500" data-testid="stop-progress-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading stops…
      </div>
    );
  }
  if (!data || !data.stops?.length) return null;

  const pill = STATUS_PILL[data.status] || STATUS_PILL.assigned;

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="stop-progress-card">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Navigation className="w-4 h-4 text-[#002FA7]" />
          <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Stop Progress
          </h3>
        </div>
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider ${pill.bg} ${pill.fg}`} data-testid="load-status-pill" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {pill.label}
        </span>
      </div>

      {err && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100 text-xs text-red-700" data-testid="stop-progress-error" role="alert" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {err}
        </div>
      )}

      <ol className="divide-y divide-slate-100">
        {data.stops.map((s, i) => {
          const state = data.progress?.[String(s.seq)] || {};
          const prevState = i > 0 ? (data.progress?.[String(data.stops[i - 1].seq)] || {}) : null;
          const prevDone = i === 0 || !!prevState?.departed_at;
          return (
            <StopRow
              key={s.seq}
              stop={s}
              state={state}
              prevDone={prevDone}
              isLast={i === data.stops.length - 1}
              readOnly={readOnly}
              pending={pending}
              onCheckIn={(action) => checkIn(s.seq, action, s.kind)}
            />
          );
        })}
      </ol>
    </div>
  );
};

const ACTION_LABELS = {
  pickup:   { arrived: 'Arrived',  worked: 'Loaded',   departed: 'Departed' },
  delivery: { arrived: 'Arrived',  worked: 'Unloaded', departed: 'Departed' },
};

const StopRow = ({ stop, state, prevDone, isLast, readOnly, pending, onCheckIn }) => {
  const arrived = !!state.arrived_at;
  const worked = !!state.worked_at;
  const departed = !!state.departed_at;
  const allDone = departed;
  const labels = ACTION_LABELS[stop.kind] || ACTION_LABELS.delivery;
  const Icon = stop.kind === 'pickup' ? Package : PackageCheck;
  const isPending = (a) => pending && pending.seq === stop.seq && pending.action === a;

  return (
    <li className="p-4" data-testid={`stop-row-${stop.seq}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center pt-0.5">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${allDone ? 'bg-[#34C759] text-white' : arrived ? 'bg-[#002FA7] text-white' : prevDone ? 'bg-white border-2 border-[#002FA7] text-[#002FA7]' : 'bg-slate-100 border-2 border-slate-200 text-slate-400'}`}>
            {allDone ? <CheckCircle className="w-4 h-4" strokeWidth={2.5} /> : <Icon className="w-4 h-4" strokeWidth={2.25} />}
          </div>
          {!isLast && <div className={`w-0.5 flex-1 mt-1 ${allDone ? 'bg-[#34C759]' : 'bg-slate-200'}`} style={{ minHeight: 28 }} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Stop {stop.seq} · {stop.kind}
            </span>
          </div>
          <p className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {stop.label || `${stop.city || ''}${stop.city && stop.state ? ', ' : ''}${stop.state || ''}` || 'Unknown location'}
          </p>

          {/* Timestamps row */}
          {(arrived || worked || departed) && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {arrived && <span className="inline-flex items-center gap-1" data-testid={`stop-${stop.seq}-arrived-ts`}><Clock className="w-3 h-3" />{labels.arrived} · {fmtDate(state.arrived_at)} {fmtTime(state.arrived_at)}</span>}
              {worked && <span className="inline-flex items-center gap-1" data-testid={`stop-${stop.seq}-worked-ts`}><Clock className="w-3 h-3" />{labels.worked} · {fmtTime(state.worked_at)}</span>}
              {departed && <span className="inline-flex items-center gap-1" data-testid={`stop-${stop.seq}-departed-ts`}><Clock className="w-3 h-3" />{labels.departed} · {fmtTime(state.departed_at)}</span>}
              {state.arrived_lat && state.arrived_lng && (
                <span className="inline-flex items-center gap-1 text-[#34C759]" title={`Arrived at ${state.arrived_lat.toFixed(4)}, ${state.arrived_lng.toFixed(4)}`}>
                  <MapPin className="w-3 h-3" /> GPS
                </span>
              )}
            </div>
          )}

          {/* Action buttons */}
          {!readOnly && !departed && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid={`stop-${stop.seq}-actions`}>
              {!arrived && (
                <CheckInBtn
                  testid={`stop-${stop.seq}-arrived`}
                  loading={isPending('arrived')}
                  disabled={!prevDone}
                  hint={!prevDone ? 'Complete the previous stop first' : 'Capture GPS + timestamp'}
                  onClick={() => onCheckIn('arrived')}
                  primary
                >
                  <Navigation className="w-3.5 h-3.5" /> {labels.arrived}
                </CheckInBtn>
              )}
              {arrived && !worked && (
                <CheckInBtn
                  testid={`stop-${stop.seq}-worked`}
                  loading={isPending('worked')}
                  onClick={() => onCheckIn('worked')}
                >
                  <Truck className="w-3.5 h-3.5" /> {labels.worked}
                </CheckInBtn>
              )}
              {arrived && (
                <CheckInBtn
                  testid={`stop-${stop.seq}-departed`}
                  loading={isPending('departed')}
                  onClick={() => onCheckIn('departed')}
                  primary
                >
                  <Zap className="w-3.5 h-3.5" /> {labels.departed}
                </CheckInBtn>
              )}
            </div>
          )}
          {!readOnly && departed && (
            <p className="mt-2 text-[11px] font-semibold text-[#34C759] inline-flex items-center gap-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <CheckCircle className="w-3 h-3" /> Stop complete
            </p>
          )}
        </div>
      </div>
    </li>
  );
};

const CheckInBtn = ({ children, onClick, loading, disabled, primary, hint, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading || disabled}
    data-testid={testid}
    title={hint || ''}
    className={`inline-flex items-center justify-center gap-1.5 px-3.5 py-2 min-h-[40px] rounded-md text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 ${
      primary
        ? 'bg-[#002FA7] text-white hover:bg-[#00247D]'
        : 'bg-white border border-slate-300 text-slate-700 hover:border-[#002FA7] hover:text-[#002FA7]'
    }`}
    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
  >
    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : children}
  </button>
);

// Silence default-export lint for the Circle import that may be unused after edits.
export const _CircleIcon = Circle;
export default StopProgressCard;
